import { useEffect, useState, useCallback, useRef } from "react";
import { Loader2, RefreshCw, Plus, Zap, ArrowUpRight, ArrowDownRight, X, TrendingUp, AlertTriangle, Clock, ShieldAlert } from "lucide-react";
import TopBar from "@/components/TopBar";
import TickerSearchDialog from "@/components/TickerSearchDialog";
import DisclaimerFooter from "@/components/DisclaimerFooter";
import TradeRegisterDialog from "@/components/TradeRegisterDialog";
import { api, formatApiErrorDetail } from "@/lib/api";

const POLL_MS = 20_000;

const VERDICT_STYLE = {
  HOLD:           { cls: "border-slate-600 bg-slate-800/40 text-slate-200",   icon: TrendingUp },
  TRAIL_STOP:     { cls: "border-cyan-500/60 bg-cyan-500/10 text-cyan-200",   icon: TrendingUp },
  TAKE_PROFIT:    { cls: "border-emerald-500/60 bg-emerald-500/15 text-emerald-200", icon: ArrowUpRight },
  EXIT_NOW:       { cls: "border-red-500/60 bg-red-500/15 text-red-200",      icon: AlertTriangle },
  TIME_EXIT:      { cls: "border-amber-500/60 bg-amber-500/15 text-amber-200",icon: Clock },
  OVERNIGHT_RISK: { cls: "border-red-500/70 bg-red-500/20 text-red-200",      icon: ShieldAlert },
};

function fmt(v, d = 2) {
  if (v == null || Number.isNaN(v)) return "—";
  return Number(v).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}

function useCountdown(targetMs) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!targetMs) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetMs]);
  if (!targetMs) return null;
  const secs = Math.max(0, Math.floor((targetMs - now) / 1000));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
}

export default function TradeMonitor() {
  const [openSearch, setOpenSearch] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [openTrades, setOpenTrades] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const prevVerdicts = useRef({});

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [{ data: opens }, { data: closed }] = await Promise.all([
        api.get("/trades", { params: { status: "OPEN", limit: 50 } }),
        api.get("/trades", { params: { status: "CLOSED", limit: 20 } }),
      ]);
      setOpenTrades(opens.trades || []);
      setClosedTrades(closed.trades || []);
      // Detect verdict changes and beep
      for (const t of opens.trades || []) {
        const prev = prevVerdicts.current[t.id];
        const cur = t.last_verdict?.verdict;
        if (prev && cur && prev !== cur) {
          beep();
        }
        prevVerdicts.current[t.id] = cur;
      }
      setError("");
    } catch (e) {
      if (!silent) setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
    const id = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  return (
    <div className="min-h-screen bg-[#080B11] flex flex-col">
      <TopBar onOpenSearch={() => setOpenSearch(true)} />
      <main className="flex-1 pb-14">
        <div className="max-w-[1400px] mx-auto p-3 sm:p-4">
          <div className="border border-[#1E293B] bg-[#0F1626] px-5 py-4 flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-amber-400" />
                <span className="mono text-[11px] uppercase tracking-widest text-amber-300">Intraday · Trade Monitor</span>
              </div>
              <h1 data-testid="trades-title" className="mono text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-100 mt-1">
                Live trades · Co-pilot verdicts
              </h1>
              <p className="mono text-[11px] text-slate-500 mt-1">
                Verdicts evaluate every 60s during market hours. Page polls every {POLL_MS/1000}s. NO orders executed.
              </p>
            </div>
            <button data-testid="trades-refresh" onClick={() => load(false)}
              className="mono text-[11px] uppercase tracking-widest px-3 h-9 border border-[#1E293B] hover:border-cyan-500/60 text-slate-300 hover:text-cyan-200 flex items-center gap-2">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Refresh
            </button>
            <button data-testid="trades-register" onClick={() => setRegisterOpen(true)}
              className="mono text-[11px] uppercase tracking-widest px-3 h-9 border border-cyan-500/60 bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/25 flex items-center gap-2">
              <Plus className="h-3.5 w-3.5" /> Register Trade
            </button>
          </div>

          {error && <div data-testid="trades-error" className="mt-3 border border-red-500/40 bg-red-500/10 p-3 mono text-[11px] text-red-300">{error}</div>}

          {/* Open trades */}
          <div className="mt-4" data-testid="open-trades-section">
            <SectionTitle title="Open Trades" count={openTrades.length} />
            {openTrades.length === 0 ? (
              <EmptyState data-testid="open-trades-empty" text="No open trades. Register one to start." />
            ) : (
              <div className="space-y-3">
                {openTrades.map((t) => <TradeCard key={t.id} trade={t} onChanged={() => load(false)} />)}
              </div>
            )}
          </div>

          {/* Closed trades table */}
          <div className="mt-6" data-testid="closed-trades-section">
            <SectionTitle title="Closed Trades" count={closedTrades.length} />
            {closedTrades.length === 0 ? (
              <EmptyState data-testid="closed-trades-empty" text="Closed trades appear here." />
            ) : (
              <div className="border border-[#1E293B] bg-[#0F1626] overflow-x-auto">
                <table className="w-full mono text-[11px]">
                  <thead className="border-b border-[#1E293B] text-slate-500">
                    <tr>
                      {["Ticker", "Dir", "Entry", "Exit", "P&L $", "R", "Duration", "Adherence"].map(h => (
                        <th key={h} className="text-left px-3 py-2 uppercase tracking-widest text-[10px]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {closedTrades.map((t) => (
                      <tr key={t.id} data-testid={`closed-trade-${t.id}`} className="border-t border-[#1E293B]">
                        <td className="px-3 py-2 font-bold text-slate-100">{t.ticker}</td>
                        <td className={`px-3 py-2 ${t.direction === "LONG" ? "text-emerald-300" : "text-red-300"}`}>{t.direction}</td>
                        <td className="px-3 py-2 tabular-nums">${fmt(t.entry_price)}</td>
                        <td className="px-3 py-2 tabular-nums">${fmt(t.close?.price)}</td>
                        <td className={`px-3 py-2 tabular-nums ${(t.close?.realized_pnl_dollars ?? 0) >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                          {t.close?.realized_pnl_dollars != null ? `$${fmt(t.close.realized_pnl_dollars)}` : "—"}
                        </td>
                        <td className={`px-3 py-2 tabular-nums ${(t.close?.r_multiple ?? 0) >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                          {t.close?.r_multiple != null ? `${fmt(t.close.r_multiple)}R` : "—"}
                        </td>
                        <td className="px-3 py-2 tabular-nums text-slate-400">{fmtDuration(t.close?.duration_ms)}</td>
                        <td className="px-3 py-2">
                          {t.close?.adherence === true && <span className="text-emerald-300">✓ {t.close?.verdict_at_close}</span>}
                          {t.close?.adherence === false && <span className="text-amber-300">✗ Late vs {t.close?.verdict_at_close}</span>}
                          {t.close?.adherence == null && <span className="text-slate-500">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      <TickerSearchDialog open={openSearch} onOpenChange={setOpenSearch} />
      <TradeRegisterDialog open={registerOpen} onOpenChange={setRegisterOpen} onCreated={() => load(false)} />
      <DisclaimerFooter />
    </div>
  );
}

function SectionTitle({ title, count }) {
  return (
    <div className="mono text-[11px] uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-2">
      {title}
      <span className="text-slate-600">({count})</span>
    </div>
  );
}

function EmptyState({ text, ...props }) {
  return (
    <div {...props} className="border border-[#1E293B] bg-[#0F1626] p-6 mono text-[11px] text-slate-500 text-center">
      {text}
    </div>
  );
}

function fmtDuration(ms) {
  if (!ms) return "—";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function TradeCard({ trade, onChanged }) {
  const v = trade.last_verdict || {};
  const meta = VERDICT_STYLE[v.verdict] || VERDICT_STYLE.HOLD;
  const Icon = meta.icon;
  const countdown = useCountdown(trade.time_exit_ts_ms);
  const [closeOpen, setCloseOpen] = useState(false);
  const [exitPrice, setExitPrice] = useState("");
  const [closing, setClosing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const closeTrade = async () => {
    if (!exitPrice) return;
    setClosing(true);
    try {
      await api.post(`/trades/${trade.id}/close`, { exit_price: Number(exitPrice) });
      setCloseOpen(false);
      onChanged?.();
    } finally { setClosing(false); }
  };

  const evaluateNow = async () => {
    try { await api.post(`/trades/${trade.id}/evaluate`); onChanged?.(); } catch (e) {}
  };

  const isLong = trade.direction === "LONG";

  return (
    <div
      data-testid={`trade-card-${trade.id}`}
      data-verdict={v.verdict || "PENDING"}
      className="border border-[#1E293B] bg-[#0F1626]"
    >
      {/* Verdict banner */}
      <div className={`border-b border-[#1E293B] px-4 py-3 flex items-start gap-3 ${meta.cls}`}>
        <Icon className="h-5 w-5 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span data-testid={`trade-verdict-${trade.id}`} className="mono text-lg font-extrabold tracking-tight">{v.verdict || "PENDING"}</span>
            {v.time_warning && <span className="mono text-[10px] px-1.5 py-0.5 border border-amber-500/60 text-amber-300 uppercase tracking-widest">TIME-WARN</span>}
            {trade.test_mode && <span className="mono text-[10px] px-1.5 py-0.5 border border-slate-600 text-slate-400 uppercase tracking-widest">TEST-MODE</span>}
          </div>
          <div className="mono text-[11px] mt-1 opacity-90">{v.reason || "Awaiting first evaluation…"}</div>
        </div>
        <button data-testid={`trade-evaluate-${trade.id}`} onClick={evaluateNow} className="mono text-[10px] uppercase tracking-widest px-2 h-7 border border-[#1E293B] bg-[#0B111C] hover:border-cyan-500/60 hover:text-cyan-300">
          Evaluate now
        </button>
      </div>

      {/* Body */}
      <div className="p-4 grid grid-cols-2 md:grid-cols-6 gap-3">
        <Stat label="Ticker" value={trade.ticker} big />
        <Stat label="Dir" value={trade.direction} tone={isLong ? "green" : "red"} />
        <Stat label="Entry" value={`$${fmt(trade.entry_price)}`} />
        <Stat label="Current" value={`$${fmt(trade.last_price)}`} />
        <Stat label="Unrl P&L" value={v.unrealized_pnl_dollars != null ? `$${fmt(v.unrealized_pnl_dollars)}` : "—"} tone={(v.unrealized_pnl_pct ?? 0) >= 0 ? "green" : "red"} />
        <Stat label="Unrl R" value={v.unrealized_r != null ? `${fmt(v.unrealized_r)}R` : "—"} tone={(v.unrealized_r ?? 0) >= 0 ? "green" : "red"} />
        <Stat label="Stop" value={`$${fmt(trade.current_stop)}`} tone="red" />
        <Stat label="T1" value={trade.targets?.t1 != null ? `$${fmt(trade.targets.t1)}` : "—"} tone={isLong ? "green" : "red"} hit={(trade.hit_targets || []).includes("T1")} />
        <Stat label="T2" value={trade.targets?.t2 != null ? `$${fmt(trade.targets.t2)}` : "—"} tone={isLong ? "green" : "red"} hit={(trade.hit_targets || []).includes("T2")} />
        <Stat label="Time-Exit" value={countdown || "—"} />
        <Stat label="Shares" value={trade.shares != null ? fmt(trade.shares, 0) : "—"} />
        <div className="flex items-end gap-2">
          <button data-testid={`trade-close-btn-${trade.id}`} onClick={() => setCloseOpen((v)=>!v)}
            className="mono text-[10px] uppercase tracking-widest px-2 h-8 border border-red-500/60 bg-red-500/10 text-red-300 hover:bg-red-500/20">Close Trade</button>
          <button data-testid={`trade-history-toggle-${trade.id}`} onClick={() => setHistoryOpen((v)=>!v)} className="mono text-[10px] uppercase tracking-widest px-2 h-8 border border-[#1E293B] bg-[#0B111C] text-slate-400 hover:text-slate-200">
            History ({(trade.verdict_history || []).length})
          </button>
        </div>
      </div>

      {closeOpen && (
        <div className="border-t border-[#1E293B] p-3 flex items-center gap-2 bg-[#0B111C]">
          <span className="mono text-[10px] uppercase tracking-widest text-slate-400">Exit Price</span>
          <input data-testid={`trade-close-price-${trade.id}`} type="number" step="0.01" value={exitPrice} onChange={(e)=>setExitPrice(e.target.value)}
            className="w-32 bg-[#0F1626] border border-[#1E293B] text-slate-100 px-2 py-1 mono text-xs" />
          <button data-testid={`trade-close-confirm-${trade.id}`} disabled={!exitPrice || closing} onClick={closeTrade}
            className="mono text-[10px] uppercase tracking-widest px-3 h-7 border border-red-500/60 bg-red-500/15 text-red-200 disabled:opacity-50">
            {closing ? "Closing…" : "Confirm Exit"}
          </button>
          <button onClick={()=>setCloseOpen(false)} className="mono text-[10px] uppercase tracking-widest px-2 h-7 border border-[#1E293B] text-slate-500">Cancel</button>
        </div>
      )}

      {historyOpen && (
        <div className="border-t border-[#1E293B] bg-[#0B111C] p-3" data-testid={`trade-history-${trade.id}`}>
          {(trade.verdict_history || []).length === 0 ? (
            <div className="mono text-[10px] text-slate-500">No verdict history yet.</div>
          ) : (
            <div className="space-y-1">
              {[...(trade.verdict_history || [])].reverse().map((h, i) => (
                <div key={i} className="mono text-[10px] text-slate-400 flex gap-3">
                  <span className="text-slate-500">{new Date(h.computed_at_ms).toLocaleTimeString()}</span>
                  <span className="uppercase tracking-widest font-bold text-slate-200">{h.verdict}</span>
                  <span className="text-slate-500 flex-1">{h.reason}</span>
                  <span className="tabular-nums text-slate-400">${fmt(h.price)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone, big, hit }) {
  const cls =
    tone === "green" ? "text-emerald-300"
    : tone === "red" ? "text-red-300"
    : "text-slate-100";
  return (
    <div className="flex flex-col">
      <span className="mono text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      <span className={`mono ${big ? "text-lg" : "text-sm"} tabular-nums font-bold ${cls} flex items-center gap-1`}>
        {value}
        {hit && <span className="mono text-[9px] px-1 py-0.5 border border-emerald-500/50 text-emerald-300 uppercase tracking-widest">Hit</span>}
      </span>
    </div>
  );
}

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine"; o.frequency.value = 880;
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    o.start(); o.stop(ctx.currentTime + 0.4);
  } catch (_) {}
}
